import { useMemo } from 'react';
import type { IModel } from '../stores/model';
import type { IMethod } from '../stores/model/metamodel/method';

/** One row of the inheritance specialisation table. */
interface Specialisation {
  superMethod: IMethod;
  candidateMethods: IMethod[];
  defaultValue: string;
}

/** Result of {@link useAddInheritanceInfo}. */
interface InheritanceInfo {
  superMethods: IMethod[];
  subMethods: IMethod[];
  abstract: boolean;
  specialisations: Specialisation[];
}

function stripEventName(method: IMethod): string {
  const eventName = method.ownerEventType.name;
  const objectName = method.ownerObjectType.name;
  return eventName.toLowerCase().replace(new RegExp(objectName.toLowerCase(), 'g'), '');
}

function suggestSpecialisation(superMethod: IMethod, candidateMethods: IMethod[]): string {
  if (superMethod.type === 'CREATE') {
    const strippedSuperEventName = stripEventName(superMethod);
    const suggestion =
      candidateMethods.find((m) => strippedSuperEventName === stripEventName(m)) ||
      candidateMethods[0];
    return suggestion != null ? suggestion.id : 'NONE';
  } else {
    return 'NONE';
  }
}

export function useAddInheritanceInfo(
  model: IModel,
  superTypeId: string,
  subTypeId: string
): InheritanceInfo {
  const result = useMemo<InheritanceInfo>(() => {
    const superType = model.metamodel.getObjectTypeById(superTypeId);
    const subType = model.metamodel.getObjectTypeById(subTypeId);
    if (superType != null && subType != null) {
      const superMethods = superType.ownedMethods
        .concat(superType.specialisedMethods)
        .sort((m1, m2) => {
          const t1 = m1.type;
          const t2 = m2.type;
          if (t1 === t2) {
            return m1.ownerEventType.name.localeCompare(m2.ownerEventType.name);
          } else if (t1 === 'CREATE') {
            return -1;
          } else if (t2 === 'CREATE') {
            return 1;
          } else if (t1 === 'END') {
            return -1;
          } else {
            return 1;
          }
        });
      const subMethods = subType.ownedMethods.slice();
      const specialisations = superMethods.map((superMethod) => {
        const candidateMethods = subMethods.filter((sm) => sm.type === superMethod.type);
        return {
          superMethod,
          candidateMethods,
          defaultValue: suggestSpecialisation(superMethod, candidateMethods),
        };
      });
      return {
        superMethods,
        subMethods: subType.ownedMethods.slice(),
        abstract: superType.abstract,
        specialisations,
      };
    } else {
      return {
        superMethods: [],
        subMethods: [],
        abstract: false,
        specialisations: [],
      };
    }
  }, [model, superTypeId, subTypeId]);
  return result;
}
